import React, { useEffect, useState } from "react";
import { Table, Card, Tag, Spin, message, Button, Grid } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { getAllB2BClients } from "../../endpoints/API";
import CreateB2BClientModal from "./CreateB2BClientModal";
import ManageB2BClientAddressesModal from "./ManageB2BClientAddressesModal";

const { useBreakpoint } = Grid;

// Generates initials + a consistent color pair from the client name
const getAvatarProps = (name = "") => {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  const palettes = [
    { bg: "#E6F1FB", color: "#0C447C" },
    { bg: "#FBEAF0", color: "#72243E" },
    { bg: "#E1F5EE", color: "#085041" },
    { bg: "#EEEDFE", color: "#3C3489" },
    { bg: "#FAEEDA", color: "#633806" },
  ];
  const idx = name.charCodeAt(0) % palettes.length;
  return { initials: initials || "?", ...palettes[idx] };
};

const B2BClientsList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createVisible, setCreateVisible] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [addressesModalVisible, setAddressesModalVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await getAllB2BClients();
      setClients(res.data || []);
    } catch (error) {
      message.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleCreatedOrUpdated = () => {
    setCreateVisible(false);
    setEditClient(null);
    fetchClients();
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "name",
      render: (name) => name || "--",
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "contact_email",
      render: (email) => email || "--",
      responsive: ["md"],
      ellipsis: true,
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      render: (n) => n || "--",
      responsive: ["sm"],
    },
    {
      title: "Point of Contact",
      responsive: ["lg"],
      render: (_, r) => (
        <>
          <div>{r.point_of_contact_name || "--"}</div>
          <div style={{ color: "#888", fontSize: 12 }}>{r.point_of_contact_email || "--"}</div>
        </>
      ),
    },
    {
      title: "Manage Address",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setSelectedClient(record);
            setAddressesModalVisible(true);
          }}
        >
          {isMobile ? "Address" : "Manage Address"}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>{active ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setEditClient(record);
            setCreateVisible(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  // ── Redesigned mobile card ──────────────────────────────────────────────────
  const MobileCard = ({ record }) => {
    const avatar = getAvatarProps(record.name);
    return (
      <div
        style={{
          background: "var(--color-background-primary, #fff)",
          border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.12))",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* Header row: avatar + name/email + status badge */}
        <div
          style={{
            padding: "14px 16px 12px",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: avatar.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 500,
              color: avatar.color,
              flexShrink: 0,
              letterSpacing: "0.04em",
            }}
          >
            {avatar.initials}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: "var(--color-text-primary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {record.name || "--"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {record.contact_email || "--"}
            </div>
          </div>

          {/* Status badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 9px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 500,
              flexShrink: 0,
              background: record.is_active ? "#EAF3DE" : "#FCEBEB",
              color: record.is_active ? "#27500A" : "#791F1F",
            }}
          >
            {record.is_active ? "Active" : "Inactive"}
          </span>
        </div>

        {/* Thin separator */}
        <div
          style={{
            height: "0.5px",
            background: "var(--color-border-tertiary, rgba(0,0,0,0.08))",
            margin: "0 16px",
          }}
        />

        {/* Detail grid */}
        <div
          style={{
            padding: "12px 16px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 16px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--color-text-tertiary)",
                marginBottom: 2,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Phone
            </div>
            <div style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
              {record.contact_number || "--"}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--color-text-tertiary)",
                marginBottom: 2,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Point of contact
            </div>
            <div style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
              {record.point_of_contact_name || "--"}
            </div>
          </div>

          {record.point_of_contact_email && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--color-text-tertiary)",
                  marginBottom: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                POC email
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--color-text-secondary)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.point_of_contact_email}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div
          style={{
            borderTop: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
            padding: "10px 16px",
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            style={{
              background: "#E6F1FB",
              color: "#0C447C",
              borderColor: "#B5D4F4",
              fontWeight: 500,
            }}
            onClick={() => {
              setSelectedClient(record);
              setAddressesModalVisible(true);
            }}
          >
            Manage address
          </Button>
          <Button
            size="small"
            onClick={() => {
              setEditClient(record);
              setCreateVisible(true);
            }}
          >
            Edit
          </Button>
        </div>
      </div>
    );
  };
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Card
      title="B2B Clients List"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size={isMobile ? "small" : "middle"}
          onClick={() => {
            setEditClient(null);
            setCreateVisible(true);
          }}
        >
          {isMobile ? "Create" : "Create B2B Client"}
        </Button>
      }
      bodyStyle={{ padding: isMobile ? "12px 8px" : "24px" }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : isMobile ? (
        <div style={{ padding: "0 4px" }}>
          {clients.length === 0 ? (
            <div
              style={{ textAlign: "center", color: "var(--color-text-tertiary)", padding: "32px 0" }}
            >
              No clients found
            </div>
          ) : (
            clients.map((record) => <MobileCard key={record.id} record={record} />)
          )}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={clients}
            pagination={{ pageSize: 5, size: "small" }}
            scroll={{ x: "max-content" }}
          />
        </div>
      )}

      {createVisible && (
        <CreateB2BClientModal
          open={createVisible}
          onClose={() => {
            setCreateVisible(false);
            setEditClient(null);
          }}
          onSuccess={handleCreatedOrUpdated}
          initialData={editClient}
        />
      )}

      {addressesModalVisible && selectedClient && (
        <ManageB2BClientAddressesModal
          open={addressesModalVisible}
          onClose={() => setAddressesModalVisible(false)}
          client={selectedClient}
        />
      )}
    </Card>
  );
};

export default B2BClientsList;